import { createTheme } from '@mui/material/styles'

import FagoWoff2 from '@/assets/fonts/Fago/fagowebpro-condensed.woff2'
import FagoBoldWoff2 from '@/assets/fonts/Fago/fagowebpro-condensedbold.woff2'
import FagoMediumWoff2 from '@/assets/fonts/Fago/fagowebpro-condensedmedium.woff2'
import { InfoIcon, ThumbsUpIcon, WarningIcon } from '@/modules/components/icons'

const white = '#ffffff'
const black = '#212529'
const defaultFontSize = 16

const fontWeightLight = 400
const fontWeightRegular = 500
const fontWeightBold = 700

export const getRem = (size: number): string => `${size / defaultFontSize}rem`

let theme = createTheme()

theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 1024,
      xl: 1200,
    },
  },
  spacing: 16,
  typography: {
    htmlFontSize: defaultFontSize,
    fontSize: defaultFontSize,
    fontWeightLight: fontWeightLight,
    fontWeightRegular: fontWeightRegular,
    fontWeightBold: fontWeightBold,
    fontFamily: 'FagoPro, Arial',
    h1: {
      fontSize: getRem(72),
      lineHeight: getRem(86),
      fontWeight: fontWeightRegular,
    },
    h2: {
      fontSize: getRem(48),
      lineHeight: getRem(57),
      fontWeight: fontWeightBold,
    },
    h3: {
      fontSize: getRem(36),
      lineHeight: getRem(43),
      fontWeight: fontWeightRegular,
    },
    h4: {
      fontSize: getRem(24),
      lineHeight: getRem(32),
      fontWeight: fontWeightBold,
    },
    h5: {
      fontSize: getRem(20),
      lineHeight: getRem(29),
      fontWeight: fontWeightBold,
    },
    subtitle1: {
      fontSize: getRem(18),
      lineHeight: getRem(25),
      fontWeight: fontWeightRegular,
    },
    body1: {
      fontSize: getRem(16),
      lineHeight: getRem(24),
      fontWeight: fontWeightRegular,
    },
    body2: {
      fontSize: getRem(16),
      lineHeight: getRem(24),
      fontWeight: fontWeightBold,
    },
    caption: {
      fontSize: getRem(14),
      lineHeight: getRem(19),
      fontWeight: fontWeightRegular,
    },
    overline: {
      fontSize: getRem(12),
      lineHeight: getRem(14),
      fontWeight: fontWeightRegular,
      textTransform: 'none',
    },
    button: {
      fontSize: getRem(16),
      lineHeight: getRem(24),
      fontWeight: fontWeightBold,
      textTransform: 'none',
    },
  },
  palette: {
    common: {
      black: black,
      white: white,
    },
    primary: {
      main: '#5E3D81',
      light: '#E2D3FB',
      contrastText: white,
    },
    secondary: {
      main: '#58585A',
      contrastText: white,
    },
    tertiary: theme.palette.augmentColor({
      color: {
        main: '#E2D3FB',
        contrastText: black,
      },
    }),
    white: theme.palette.augmentColor({
      color: {
        main: white,
        contrastText: black,
      },
    }),
    active: theme.palette.augmentColor({
      color: {
        main: '#fcbf1e',
        contrastText: black,
      },
    }),
    success: {
      main: '#198754',
      light: '#F4F7E4',
      contrastText: white,
    },
    warning: {
      main: '#CA9918',
      light: '#FDEED4',
      contrastText: white,
    },
    error: {
      main: '#DC3545',
      light: '#F8ECF5',
      contrastText: white,
    },
    info: {
      main: '#1A7EA3',
      light: '#E1EDEF',
      contrastText: white,
    },
    customGrey: {
      darker: '#58585A',
      dark: '#969696',
      light: '#CDCDCD',
      lighter: '#EEEFF0',
    },
    background: {
      paper: white,
      default: '#F4F4F4',
    },
    text: {
      primary: black,
    },
    shadows: [
      'none',
      '0px 0px 2px 0px rgba(0, 0, 0, 0.06), 0px 2px 2px 0px rgba(0, 0, 0, 0.04), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
      '0px 2px 4px 0px rgba(0, 0, 0, 0.06), 0px 3px 4px 0px rgba(0, 0, 0, 0.04), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
      '0px 3px 3px 0px rgba(0, 0, 0, 0.06), 0px 3px 4px 0px rgba(0, 0, 0, 0.04), 0px 1px 8px 0px rgba(0, 0, 0, 0.12)',
      '0px 2px 4px 0px rgba(0, 0, 0, 0.06), 0px 4px 5px 0px rgba(0, 0, 0, 0.04), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
      '0px 6px 10px 0px rgba(0, 0, 0, 0.06), 0px 1px 18px 0px rgba(0, 0, 0, 0.04), 0px 3px 5px 0px rgba(0, 0, 0, 0.12)',
      '0px 8px 10px 0px rgba(0, 0, 0, 0.06), 0px 3px 14px 0px rgba(0, 0, 0, 0.04), 0px 4px 5px 0px rgba(0, 0, 0, 0.12)',
      ' 0px 9px 12px 0px rgba(0, 0, 0, 0.06), 0px 3px 16px 0px rgba(0, 0, 0, 0.04), 0px 5px 6px 0px rgba(0, 0, 0, 0.12)',
      '0px 12px 17px 0px rgba(0, 0, 0, 0.06), 0px 5px 22px 0px rgba(0, 0, 0, 0.04), 0px 7px 8px 0px rgba(0, 0, 0, 0.12)',
      '0px 16px 24px 0px rgba(0, 0, 0, 0.06), 0px 6px 30px 0px rgba(0, 0, 0, 0.04), 0px 8px 10px 0px rgba(0, 0, 0, 0.12)',
      '0px 24px 38px 0px rgba(0, 0, 0, 0.06), 0px 9px 46px 0px rgba(0, 0, 0, 0.04), 0px 11px 15px 0px rgba(0, 0, 0, 0.12)',
    ],
  },
})

theme = createTheme(theme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'FagoPro';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('FagoPro'), local('FagoPro-Regular'), url(${FagoWoff2}) format('woff2');
        }
        
        @font-face {
          font-family: 'FagoPro';
          font-style: normal;
          font-display: swap;
          font-weight: 500;
          src: local('FagoProMedium'), local('FagoPro-Medium'), url(${FagoMediumWoff2}) format('woff2');
        }
        
        @font-face {
          font-family: 'FagoPro';
          font-style: normal;
          font-display: swap;
          font-weight: 700;
          src: local('FagoProBold'), local('FagoPro-Bold'), url(${FagoBoldWoff2}) format('woff2');
        }
      `,
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          [theme.breakpoints.up('sm')]: {
            minHeight: '76px',
            paddingLeft: theme.spacing(1.5),
            paddingRight: theme.spacing(1.5),
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          position: 'relative',
          top: 0,
          left: 0,
          fontSize: getRem(16),
          lineHeight: getRem(24),
          fontWeight: fontWeightBold,
          color: theme.palette.common.black,
          transform: 'none',
          marginBottom: theme.spacing(0.125),
          whiteSpace: 'normal',
          '&:not(:has(.MuiButtonBase-root))': {
            marginBottom: theme.spacing(0.5),
          },
          '&.Mui-focused': {
            color: theme.palette.common.black,
          },
        },
        sizeSmall: {
          ...theme.typography.caption,
          minHeight: theme.spacing(1.5),
          marginBottom: theme.spacing(0.25),
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          width: '100%',
        },
        multiline: {
          padding: theme.spacing(0.25, 0.75),
        },
        input: {
          fontSize: getRem(16),
          lineHeight: getRem(24),
          minHeight: '40px',
          boxSizing: 'border-box',
          padding: theme.spacing(0.25, 0.75),
          '&.MuiInputBase-inputAdornedStart': {
            paddingLeft: 0,
          },
          '&.MuiInputBase-inputAdornedEnd': {
            paddingRight: 0,
          },
        },
        notchedOutline: {
          top: 0,
          '& legend': {
            display: 'none',
          },
        },
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          background: theme.palette.common.white,
          '&.Mui-readOnly': {
            background: theme.palette.customGrey.lighter,
          },
        },
        multiline: {
          '& > textarea': {
            minHeight: 'initial',
            paddingLeft: 0,
          },
        },
        adornedStart: {
          paddingLeft: theme.spacing(0.75),
        },
        adornedEnd: {
          paddingRight: theme.spacing(0.75),
        },
        inputAdornedStart: {
          paddingLeft: 0,
        },
        inputAdornedEnd: {
          paddingRight: 0,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          alignItems: 'flex-start',
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: theme.palette.common.black,
          marginRight: '-6px',
          '.MuiButtonBase-root': {
            padding: '2px',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          border: '1px solid ' + theme.palette.customGrey.light,
          borderRadius: theme.shape.borderRadius,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: getRem(20),
        },
        fontSizeMedium: {
          fontSize: getRem(24),
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(1.5),
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          '&[data-popper-placement*="left"]': {
            '& .MuiTooltip-arrow': {
              marginRight: '-8px',
              height: '12px',
              width: '8px',
            },
          },
          '&[data-popper-placement*="top"]': {
            '& .MuiTooltip-arrow': {
              height: '8px',
              width: '12px',
              marginBottom: '-8px',
            },
          },
        },
        tooltip: {
          ...theme.typography.body1,
          display: 'flex',
          alignItems: 'center',
          background: theme.palette.common.black,
          padding: theme.spacing(0.25, 0.75),
          minHeight: '34px',
        },
        tooltipPlacementTop: {
          '.MuiTooltip-popper[data-popper-placement*="top"] &': {
            marginBottom: '6px',
          },
        },
        tooltipPlacementLeft: {
          '.MuiTooltip-popper[data-popper-placement*="left"] &': {
            marginRight: '6px',
          },
        },
        arrow: {
          color: theme.palette.common.black,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          display: 'flex',
          '&:not(:last-child)': {
            marginBottom: theme.spacing(1.5),
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: theme.spacing(-0.625),
          marginRight: 0,
          maxWidth: '100%',
          alignItems: 'flex-start',
          padding: theme.spacing(0.375, 0, 0, 0),
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...theme.typography.body1,
          color: theme.palette.common.black,
          fontWeight: fontWeightBold,
          marginBottom: 0,
          '&.Mui-focused': {
            color: theme.palette.common.black,
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
          },
          '&:not(:empty):not(:has(.MuiButtonBase-root))': {
            marginBottom: theme.spacing(0.375),
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          padding: theme.spacing(0.375),
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          ...theme.typography.caption,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(0.25, 0.5),
          minWidth: '97px',
          height: 'auto',
        },
        label: {
          padding: 0,
        },
        colorDefault: {
          color: theme.palette.customGrey.darker,
          background: theme.palette.customGrey.lighter,
          borderColor: theme.palette.customGrey.light,
        },
        colorSuccess: {
          background: theme.palette.success.light,
        },
        colorWarning: {
          color: theme.palette.common.black,
          background: theme.palette.warning.light,
        },
        colorError: {
          background: theme.palette.error.light,
        },
        colorPrimary: {
          background: theme.palette.primary.light,
          color: theme.palette.common.black,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: theme.palette.customGrey.dark,
          padding: theme.spacing(0.625),
          marginTop: `calc(${theme.spacing(-0.625)} - 1px)`,
          '&.MuiCheckbox-indeterminate': {
            color: theme.palette.customGrey.dark,
          },
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          flexWrap: 'nowrap',
          alignItems: 'flex-start',
          ['&.MuiRadioGroup-row']: {
            gap: theme.spacing(1.5),
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: theme.palette.customGrey.dark,
          padding: theme.spacing(0.625),
          marginTop: `calc(${theme.spacing(-0.625)} - 1px)`,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        color: 'primary',
        size: 'small',
      },
      styleOverrides: {
        root: {
          ...theme.typography.button,
          padding: theme.spacing(0.5, 1.25),
          borderRadius: 0,
          minHeight: '46px',
          minWidth: '46px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        sizeSmall: {
          ...theme.typography.button,
          padding: theme.spacing(0.5, 1),
          minHeight: '40px',
          minWidth: '40px',
        },
        sizeLarge: {
          fontSize: getRem(18),
          padding: theme.spacing(0.75, 1.5),
          minHeight: '55px',
          minWidth: '55px',
          '.MuiButton-endIcon>*:nth-of-type(1),.MuiButton-startIcon>*:nth-of-type(1)':
            {
              fontSize: getRem(24),
            },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          color: theme.palette.customGrey.darker,
          '&.Mui-selected': {
            color: theme.palette.common.black,
            background: theme.palette.primary.light,
          },
          '&:not(:last-child)': {
            borderBottom: '1px solid ' + theme.palette.customGrey.light,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          minWidth: '620px',
        },
        paperFullWidth: {
          width: 'calc(100% - 96px)',
          maxHeight: '672px',
          maxWidth: '900px',
          height: 'calc(100% - 96px)',
        },
      },
    },
    MuiDialogTitle: {
      defaultProps: {
        variant: 'h5',
      },
      styleOverrides: {
        root: {
          padding: theme.spacing(1.5, 1, 1, 1.5),
          '& + .MuiDialogContent-root': {
            paddingTop: theme.spacing(0.75),
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          ...theme.typography.subtitle1,
          padding: theme.spacing(0.75, 2.5, 2.5, 1.5),
          color: theme.palette.common.black,
          '&:not(:last-child)': {
            paddingBottom: theme.spacing(1.5),
          },
          ['ul']: {
            paddingLeft: 0,
            margin: 0,
            listStyle: 'none',
            '&:not(:last-child)': {
              marginBottom: theme.spacing(1.5),
            },
          },
          ['li']: {
            position: 'relative',
            paddingLeft: theme.spacing(1),
            '&:before': {
              content: '"·"',
              position: 'absolute',
              top: '0px',
              left: '-2px',
              fontSize: getRem(24),
              color: 'black',
            },
            '&:not(:last-child)': {
              marginBottom: theme.spacing(1.5),
            },
          },
          ['p']: {
            marginTop: 0,
            marginBottom: 0,
            '&:not(:last-child)': {
              marginBottom: theme.spacing(1.5),
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          alignItems: 'flex-start',
          paddingRight: theme.spacing(0.5),
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1.5, 2.5, 3, 1.5),
          justifyContent: 'flex-start',
          '&>:not(style)~:not(style)': {
            marginLeft: theme.spacing(1),
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.customGrey.light,
          marginBottom: theme.spacing(1.5),
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          background: theme.palette.common.white,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: theme.spacing(0.5),
          minHeight: '52px',
          fontWeight: fontWeightBold,
          color: theme.palette.customGrey.dark,
          flex: '1 1 auto',
          '&.Mui-selected': {
            color: theme.palette.common.black,
          },
          '&.Mui-disabled': {
            color: theme.palette.customGrey.light,
          },
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: <ThumbsUpIcon fontSize="inherit" />,
          error: <WarningIcon fontSize="inherit" />,
          warning: <WarningIcon fontSize="inherit" />,
          info: <InfoIcon fontSize="inherit" />,
        },
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: theme.spacing(0, 1),
          fontSize: getRem(16),
          fontWeight: fontWeightBold,
          height: '40px',
          zIndex: '10',
          '&.MuiAlert-standardSuccess': {
            color: theme.palette.common.white,
            background: theme.palette.success.main,
            '& .MuiAlert-icon': {
              color: theme.palette.common.white,
              marginTop: theme.spacing(0.2),
            },
          },
          '&.MuiAlert-standardError': {
            color: theme.palette.common.white,
            background: theme.palette.error.main,
            '& .MuiAlert-icon': {
              color: theme.palette.common.white,
              marginTop: theme.spacing(0.2),
            },
          },
          '&.MuiAlert-standardInfo': {
            color: theme.palette.common.white,
            background: theme.palette.info.main,
            '& .MuiAlert-icon': {
              color: theme.palette.common.white,
              marginTop: theme.spacing(0.2),
            },
          },
          '&.MuiAlert-standardWarning': {
            color: theme.palette.common.white,
            background: theme.palette.warning.main,
            '& .MuiAlert-icon': {
              color: theme.palette.common.white,
              marginTop: theme.spacing(0.2),
            },
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid ' + theme.palette.customGrey.light,
          ['&:before']: {
            display: 'none',
          },
          ['&.Mui-expanded']: {
            margin: 0,
            borderBottom: 'none',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: theme.spacing(0.5, 0, 1.5),
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          ...theme.typography.subtitle1,
          fontWeight: theme.typography.fontWeightBold,
          background: theme.palette.customGrey.lighter,
          minHeight: 'initial',
          padding: theme.spacing(0, 1.5),
          '&.Mui-expanded': {
            minHeight: 'initial',
          },
          '&:hover': {
            background: theme.palette.customGrey.light,
          },
        },
        content: {
          margin: theme.spacing(1, 0),
          '&.Mui-expanded': {
            margin: theme.spacing(1, 0),
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: theme.palette.customGrey.darker,
          p: {
            margin: theme.spacing(0, 0, 1.5),
          },
        },
      },
    },
  },
})

export default theme
